import type { PiletMetadata } from 'piral-core';

import type { AppShellType, FeedConfig } from '../types';
import { TENANT_QUERY_PARAM } from './constants';
import {
  getLocalStorageItem,
  getSessionStorageItem,
  setSessionStorageItem,
  StorageKey,
} from './storage';

interface FeedResponse {
  items: Array<PiletMetadata>;
}

interface CachedFeedMetadata {
  urls: Array<string>;
  scope: string;
  expiresAt: number;
  items: Array<PiletMetadata>;
}

interface SessionCacheIdentity {
  user?: {
    accountId?: number;
  };
}

const FEED_METADATA_CACHE_TTL_MS = 5 * 60 * 1000;

export async function fetchPilets(urls: Array<string>): Promise<Array<PiletMetadata>> {
  const cacheScope = getFeedCacheScope();
  const cachedItems = getCachedFeedMetadata(urls, cacheScope);

  if (cachedItems) {
    await waitForMinimumLoadTime();
    return cachedItems;
  }

  // show skeletons 500ms during pilet load
  const [, ...items] = await Promise.all([
    waitForMinimumLoadTime(),
    ...urls.map(async (url) => {
      const res = await fetch(url);

      if (res.ok) {
        const data: FeedResponse = await res.json();
        return data.items;
      }

      if (res.status === 404) {
        // in case of a feed not found we should just return an empty array
        return [];
      }

      throw new Error(`Failed to fetch pilets from ${url}: ${res.status} - ${res.statusText}`);
    }),
  ]);

  const pilets = items.flat();
  setCachedFeedMetadata(urls, cacheScope, pilets);

  return pilets;
}

function waitForMinimumLoadTime(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 500);
  });
}

function getCachedFeedMetadata(
  urls: Array<string>,
  scope: string,
): Array<PiletMetadata> | undefined {
  try {
    const cachedValue = getSessionStorageItem(StorageKey.PILET_METADATA);

    if (!cachedValue) {
      return undefined;
    }

    const cached: CachedFeedMetadata = JSON.parse(cachedValue);

    if (
      cached.expiresAt > Date.now() &&
      JSON.stringify([...cached.urls].sort()) === JSON.stringify([...urls].sort()) &&
      cached.scope === scope &&
      Array.isArray(cached.items)
    ) {
      return cached.items;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function setCachedFeedMetadata(
  urls: Array<string>,
  scope: string,
  items: Array<PiletMetadata>,
): void {
  try {
    const cached: CachedFeedMetadata = {
      urls,
      scope,
      expiresAt: Date.now() + FEED_METADATA_CACHE_TTL_MS,
      items,
    };
    setSessionStorageItem(StorageKey.PILET_METADATA, JSON.stringify(cached));
  } catch {
    // Storage can be unavailable or full; loading pilets must still succeed.
  }
}

function getFeedCacheScope(): string {
  const tenantQuery = new URLSearchParams(location.search).get(TENANT_QUERY_PARAM);
  const sessionIdentity = getSessionCacheIdentity();
  const activeTenantId = getLocalStorageItem(StorageKey.ACTIVE_TENANT_ID);

  return JSON.stringify({
    accountId: sessionIdentity?.user?.accountId,
    activeTenantId,
    tenantQuery,
  });
}

function getSessionCacheIdentity(): SessionCacheIdentity | undefined {
  try {
    const sessionData = getSessionStorageItem(StorageKey.SESSION_DATA);
    return sessionData ? (JSON.parse(sessionData) as SessionCacheIdentity) : undefined;
  } catch {
    return undefined;
  }
}

export function getFeeds(feeds: FeedConfig, type: AppShellType): Array<string> {
  if (Array.isArray(feeds)) {
    return type === 'full' ? feeds : [];
  }

  if (type === 'static') {
    return [];
  }

  if (feeds) {
    const typeFeeds = feeds[type];

    if (typeFeeds && Array.isArray(typeFeeds)) {
      return typeFeeds;
    }
  }

  return [];
}

export function createFeedUrls(
  environment: string,
  feedEndpoint: string,
  feeds: Array<string>,
): Array<string> {
  const searchParams = new URLSearchParams(location.search);

  return feeds.map((feed) => {
    const url = `${feedEndpoint}/${environment}-${feed}`;
    return searchParams.has(feed) ? `${url}?tag=${searchParams.get(feed)}` : url;
  });
}
